/* File: src/components/ui/AppHeader.js */
import React from 'react';
import NotificationTray from './NotificationTray';
import { useNotifications } from '../../contexts/NotificationContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';

/**
 * AppHeader Component
 * Displays the application logo, title, notification tray,
 * and a hamburger menu button that toggles the sidebar mobile menu.
 *
 * Props:
 * - authUser: {object | null} The currently authenticated Firebase user object, or null.
 * - toggleMobileMenu: {function} Function to toggle the sidebar mobile menu.
 */
const AppHeader = ({ authUser, toggleMobileMenu }) => {
    // Get navigate function from react-router-dom for navigation
    const navigate = useNavigate();

    // --- Notification Context Handling ---
    let notificationContextAvailable = false;
    try {
        useNotifications();
        notificationContextAvailable = true;
    } catch (e) {
        notificationContextAvailable = false;
    }
    
    // Only get the context if it's available
    const { unreadCount } = notificationContextAvailable ? useNotifications() : { unreadCount: 0 };
    // --- End Notification Context Handling ---

    return (
        // Header element: white background, bottom border, shadow, sticky position, z-index
        <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
            {/* Navigation container: responsive padding */}
            <nav className="px-4 sm:px-6 lg:px-8">
                {/* Flex container for alignment: items centered vertically, space between left/right sides, fixed height */}
                <div className="flex items-center justify-between h-16">

                    {/* === Left Side: Logo and Title Combined === */}
                    <div className="flex-shrink-0">
                        {/* Link wraps both logo and text */}
                        <Link to="/dashboard" className="flex items-center" title="Go to Dashboard">
                            {/* N² Logo */}
                            <div className="h-8 w-8 mr-2 sm:mr-3 bg-gradient-to-br from-indigo-600 to-indigo-800 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                                <span
                                    className="font-tomorrow font-semibold text-lg text-white"
                                    style={{ fontFamily: 'Tomorrow, sans-serif', fontWeight: 600 }}
                                >
                                    N²
                                </span>
                            </div>
                            {/* Title Text - Responsive */}
                            <span className="font-bold text-lg sm:text-xl text-gray-800 font-tomorrow whitespace-nowrap">
                                Dual N-Back
                            </span>
                        </Link>
                    </div>
                    {/* === End Left Side === */}

                    {/* === Right Side: Icons and Controls === */}
                    <div className="flex items-center space-x-3 sm:space-x-4">

                        {/* Notification Tray Component */}
                        {/* Render only if user is logged in and context is available */}
                        {(authUser && notificationContextAvailable) && (
                            <NotificationTray 
                                navigate={navigate} // Pass the navigate function to the NotificationTray
                            />
                        )}

                        {/* === Hamburger Menu Button (Mobile Only) === */}
                        {authUser && (
                            <button
                                onClick={toggleMobileMenu}
                                className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 lg:hidden" // Show only below lg breakpoint
                                aria-controls="mobile-menu"
                                aria-label="Open main menu"
                            >
                                <span className="sr-only">Open main menu</span>
                                <FontAwesomeIcon icon={faBars} className="block h-6 w-6" aria-hidden="true" />
                            </button>
                        )}
                        {/* === End Hamburger Menu Button === */}

                    </div>
                    {/* === End Right Side === */}

                </div> {/* End main flex container */}
            </nav>
        </header>
    );
};

export default AppHeader;